import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import style from "./LandingHero.module.css";
import Icon from "@/ui/icon/Icon";
import Button from "@/ui/button/Button";
import { useHeroVideos } from "@/features/landing/hooks/useHeroVideos";
import CourseCursor from "@/features/landing/components/courseCursor/CourseCursor";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { useLocalized } from "@/i18n/useLocalized";

const HERO_MUTED_KEY = "dekamas:hero-muted";

const COURSE_IN_MS = 1500;
const COURSE_OUT_MS = 7500;

const LECTURER_IN_MS = 10000;
const LECTURER_OUT_MS = 18000;

// Two rates over the same playback API: a tight one while we wait for the
// first frame, a lazy one for the rest of the lesson, where all we watch for
// is the playhead running out.
const START_POLL_MS = 250;
const END_POLL_MS = 1000;

// How close to the duration counts as finished. Wide enough that a lazy tick
// cannot step over the end of the video and miss it, and it doubles as cover:
// the player is torn down this far short of the end, so Bunny's own replay
// overlay never gets a frame on screen.
const ENDED_EPSILON_S = 1.5;

function LandingHero() {
	const { t } = useTranslation();
	const l = useLocalized();
	const heroVideos = useHeroVideos();

	// Which lesson of the run is on screen, and whether the run is over. Once
	// it is, the panel keeps the last video's thumbnail instead of looping back
	// to the top.
	const [index, setIndex] = useState(0);
	const [finished, setFinished] = useState(false);

	const heroVideo = heroVideos[index] ?? null;

	// The visitor's remembered choice. The player itself always starts muted —
	// browsers block autoplay outright otherwise — so this is applied only once
	// playback is confirmed running, not at mount.
	const [storedMuted, setStoredMuted] = useLocalStorageState(
		true,
		HERO_MUTED_KEY
	);
	const [muted, setMuted] = useState(true);
	const appliedStoredMuted = useRef(false);
	const [playerReady, setPlayerReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [showCourse, setShowCourse] = useState(false);
	const [showLecturer, setShowLecturer] = useState(false);
	const playerRef = useRef(null);
	// State, not a ref: CourseCursor attaches its listeners in an effect keyed
	// on this element, and the panel only exists once the hero video resolves.
	const [panelEl, setPanelEl] = useState(null);

	// Read by `advance`, which is created once and so cannot close over either
	// value. A ref rather than a dependency: rebuilding the callback would
	// restart the poll interval that calls it, every time a lesson changed.
	const indexRef = useRef(0);
	const totalRef = useRef(0);

	useEffect(() => {
		totalRef.current = heroVideos.length;
	}, [heroVideos.length]);

	// Called when the current lesson runs out. Created once, so it cannot read
	// `index` out of a closure — `indexRef` is the authoritative position and
	// moves here, ahead of the state catching up.
	const advance = useCallback(() => {
		const next = indexRef.current + 1;

		if (next >= totalRef.current) {
			setFinished(true);
			setIsPlaying(false);
			return;
		}

		indexRef.current = next;
		setIndex(next);

		// The next player announces itself through `playerReady`; until it
		// does, this video is back to showing its poster with no overlays.
		setIsPlaying(false);
		setShowCourse(false);
		setShowLecturer(false);
	}, []);

	// Bunny's embed is controlled over postMessage through Playerjs — the same
	// bridge CourseWatching uses. The script is loaded globally in index.html.
	const iframeRef = useCallback((iframe) => {
		if (!iframe) {
			// The iframe is keyed on the video id, so advancing tears one player
			// down and mounts the next. Drop the stale handle: every effect
			// below waits on `playerReady` going back up.
			playerRef.current = null;
			setPlayerReady(false);
			return;
		}

		if (!window.playerjs) return;

		const player = new window.playerjs.Player(iframe);
		player.on("ready", () => {
			playerRef.current = player;
			setPlayerReady(true);
		});
	}, []);

	// The course thumbnail covers the player until real frames are on screen,
	// and the same poll then watches for the lesson running out. `ready` only
	// means the player loaded — at that point it is still showing its own poster
	// and play button. A playhead past zero is the first proof that video is
	// actually rendering.
	//
	// This polls getCurrentTime and getDuration rather than listening for an
	// `ended` event: both are documented methods of Bunny's playback API,
	// whereas which events their player emits is not, and a listener that never
	// fired would strand the hero on a video that had already finished.
	//
	// If autoplay is blocked the playhead stays at zero, the thumbnail simply
	// remains and nothing advances — which is the behaviour we want, rather than
	// exposing the player's chrome.
	useEffect(() => {
		if (!playerReady || finished) return;

		// A player that is ready has not necessarily loaded metadata yet, so
		// this keeps asking until it gets a number back.
		let duration = 0;

		const poll = setInterval(
			() => {
				const player = playerRef.current;
				if (!player) return;

				if (duration <= 0) {
					player.getDuration((value) => {
						if (value > 0) duration = value;
					});
				}

				player.getCurrentTime((seconds) => {
					if (seconds > 0 && !isPlaying) setIsPlaying(true);

					// A duration we never manage to read leaves this false
					// forever, which lands on the old behaviour — the lesson
					// plays out and the hero stays on it — rather than cutting
					// a video short on a bad reading.
					if (duration > 0 && seconds >= duration - ENDED_EPSILON_S) {
						advance();
					}
				});
			},
			isPlaying ? END_POLL_MS : START_POLL_MS
		);

		return () => clearInterval(poll);
	}, [playerReady, isPlaying, finished, advance]);

	// Apply the remembered preference the first time playback starts, and only
	// then. A ref guards it so this runs once: re-applying it would fight the
	// fallback below, which can set muted back to true.
	useEffect(() => {
		if (!isPlaying || appliedStoredMuted.current) return;

		appliedStoredMuted.current = true;
		if (!storedMuted) setMuted(false);
	}, [isPlaying, storedMuted]);

	// The course being played, tagged top-left: in at 1.5s, out at 7.5s. Timed
	// the same way as the lecturer card below and deliberately clear of it, so
	// the panel never carries two overlays at once and neither outstays the
	// shot it belongs to. Keyed on the name for the same reason: an object
	// dependency changes identity every render and restarts the timers.
	const courseName = l(heroVideo, "courseName") || null;

	useEffect(() => {
		if (!isPlaying || !courseName) return;

		const show = setTimeout(() => setShowCourse(true), COURSE_IN_MS);
		const hide = setTimeout(() => setShowCourse(false), COURSE_OUT_MS);

		return () => {
			clearTimeout(show);
			clearTimeout(hide);
		};
	}, [isPlaying, courseName, index]);

	// Lower third crediting the lecturer: in at 10s, out at 18s. Timed from the
	// moment playback actually starts rather than from mount, so it lands ten
	// seconds into the video no matter how long the player took to load.
	//
	// Keyed on the lecturer's name rather than the object: an object dependency
	// changes identity on every render, which re-ran this effect, restarted the
	// timers, and made the card reappear on a loop instead of showing once.
	const lecturerName = heroVideo?.lecturer?.name ?? null;

	useEffect(() => {
		if (!isPlaying || !lecturerName) return;

		const show = setTimeout(() => setShowLecturer(true), LECTURER_IN_MS);
		const hide = setTimeout(() => setShowLecturer(false), LECTURER_OUT_MS);

		return () => {
			clearTimeout(show);
			clearTimeout(hide);
		};
	}, [isPlaying, lecturerName, index]);

	useEffect(() => {
		if (!playerReady || !playerRef.current) return;

		const player = playerRef.current;

		if (muted) {
			player.mute();
			return;
		}

		player.unmute();

		// Browsers allow a muted video to autoplay, but unmuting one without a
		// user gesture can make them pause it instead — which is exactly what
		// restoring a saved "unmuted" preference does on a fresh page load. If
		// that happens, fall back to muted and keep playing rather than leaving
		// a frozen frame. The stored preference is deliberately left alone: the
		// visitor still wants sound, the browser simply refused this time.
		const recover = setTimeout(() => {
			player.getPaused((paused) => {
				if (!paused) return;

				player.mute();
				player.play();
				setMuted(true);
			});
		}, 700);

		return () => clearTimeout(recover);
	}, [muted, playerReady]);

	return (
		<section className={style.hero}>
			<div className={style.inner}>
				<div className={style.copy}>
					<div className={style.eyebrow}>{t("landing:hero.eyebrow")}</div>
					<h1 className={style.title}>{t("landing:hero.title")}</h1>
					<p className={style.lede}>{t("landing:hero.lede")}</p>
					<div className={style.actions}>
						<Link to="/plans" className={style.primary}>
							{t("landing:hero.plans")}
							<Icon icon="fa-arrow-right" />
						</Link>
						{/* The same outline button the FAQ ends on, so the
						    page's two secondary calls to action read as one
						    thing. */}
						<Button
							type="outline"
							to="/register"
							className={style.secondary}
						>
							<span>{t("landing:hero.freeCourses")}</span>
							<Icon icon="fa-unlock" />
						</Button>
					</div>
				</div>
			</div>

			<div className={style.media}>
				{/* The panel runs up behind the header's search field; this
				    pane blurs its own top strip so the field has something
				    soft to sit on. A backdrop blur rather than a second copy
				    of the poster: it takes the video itself, so there is no
				    seam where two differently-cropped images meet. */}
				<div className={style.ambient} aria-hidden="true"></div>

				{/* The whole panel opens the course it is playing. The iframe
				    already has pointer-events off — Playerjs drives it over
				    postMessage — so this link, not the player, is what the
				    pointer meets. It sits under .topRow, which keeps the mute
				    button clickable and hands the badge a pointerleave the
				    moment the pointer crosses onto it. */}
				{heroVideo?.courseSlug && (
					<Link
						ref={setPanelEl}
						to={`/courses/${heroVideo.courseSlug}`}
						className={style.panelLink}
						aria-label={
							courseName
								? t("landing:hero.learnMoreAbout", {
										course: courseName,
									})
								: t("landing:hero.learnMore")
						}
					/>
				)}

				{heroVideo && !finished && (
					<iframe
						key={heroVideo.videoId}
						ref={iframeRef}
						className={style.video}
						title={
							l(heroVideo, "courseName")
								? `${l(heroVideo, "courseName")} — ${l(heroVideo, "unitName")}`
								: l(heroVideo, "unitName")
						}
						src={
							`https://iframe.mediadelivery.net/embed/${heroVideo.libraryId}/${heroVideo.videoId}` +
							`?autoplay=true&loop=false&muted=true&preload=true&responsive=true`
						}
						allow="autoplay; encrypted-media"
						loading="lazy"
					></iframe>
				)}

				{heroVideo?.thumbnail && (
					<img
						className={`${style.poster} ${
							isPlaying ? style.posterHidden : ""
						}`}
						src={heroVideo.thumbnail}
						alt=""
						aria-hidden="true"
					/>
				)}

				{/* Top row of the panel: the course being played on the left,
				    the mute toggle on the right. Same inset as the lecturer
				    lower third in the opposite corner. */}
				{heroVideo && (
					<div className={style.topRow}>
						{courseName && (
							<div
								className={`${style.courseTag} ${
									showCourse ? style.courseTagIn : ""
								}`}
								aria-hidden={!showCourse}
							>
								{courseName}
							</div>
						)}

						{isPlaying && (
							<button
								type="button"
								className={style.muteButton}
								onClick={() => {
									const next = !muted;
									setMuted(next);
									setStoredMuted(next);
								}}
								aria-label={
									muted
										? t("landing:hero.soundOn")
										: t("landing:hero.soundOff")
								}
							>
								<Icon
									icon={
										muted
											? "fa-volume-xmark"
											: "fa-volume-high"
									}
									iconType="fa-solid"
								/>
							</button>
						)}
					</div>
				)}

				{heroVideo?.lecturer && (
					<div
						className={`${style.lecturer} ${
							showLecturer ? style.lecturerIn : ""
						}`}
						aria-hidden={!showLecturer}
					>
						{heroVideo.lecturer.pictureUrl && (
							<img
								className={style.lecturerPhoto}
								src={heroVideo.lecturer.pictureUrl}
								alt=""
							/>
						)}
						<div className={style.lecturerText}>
							<div className={style.lecturerName}>
								{heroVideo.lecturer.name}
							</div>
							{l(heroVideo.lecturer, "jobTitle") && (
								<div className={style.lecturerRole}>
									{l(heroVideo.lecturer, "jobTitle")}
								</div>
							)}
						</div>
					</div>
				)}

				<div className={style.fade}></div>
			</div>

			<CourseCursor target={panelEl} label={t("landing:hero.cursor")} />
		</section>
	);
}

export default LandingHero;
